import { useCallback, useRef, useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { ModalDialog } from 'core-components/library/modalDialog'

import { PLANNING_SMB_TRANSLATION_KEYS } from '../../configs'
import { TSmbSplitResult } from '../../model/types'
import { PlanningSmbStatus } from '../planningSmbCell/PlanningSmbCell'

import {
  IPlanningSmbContentProps,
  PlanningSmbContent,
} from './components/planningSmbContent/PlanningSmbContent'

import styles from './PlanningSmbModal.module.scss'

const KEYS = PLANNING_SMB_TRANSLATION_KEYS.modal

export interface IPlanningSmbModalProps extends Omit<IPlanningSmbContentProps, 'onSummaryChange'> {
  show: boolean
  offeringSummary: TSmbSplitResult
  offeringBaseline: TSmbSplitResult
  onClose: () => void
}

export const PlanningSmbModal = ({
  show,
  offeringSummary = { isEmpty: true },
  offeringBaseline = { isEmpty: true },
  onClose,
  ...contentProps
}: IPlanningSmbModalProps) => {
  // ModalDialog's global Escape handler cannot see state owned by its content. Keep a synchronous
  // ref at this boundary so no close path can unmount the progress UI while save/reload is pending.
  const isSavingRef = useRef(false)
  const [currentOfferingSummary, setCurrentOfferingSummary] = useState(offeringSummary)
  const handleSavingChange = useCallback((isSaving: boolean) => {
    isSavingRef.current = isSaving
  }, [])
  const handleClose = useCallback(() => {
    if (!isSavingRef.current) {
      onClose()
    }
  }, [onClose])

  return (
    <ModalDialog<IPlanningSmbContentProps>
      show={show}
      size="xl"
      classes={{
        window: styles.window,
        header: styles.modalHeader,
        headerBodyContent: styles.headerBody,
        body: styles.body,
        closeButton: styles.closeButton,
      }}
      closeOnOutsideClick={false}
      header={() => (
        <div className={styles.headerLayout}>
          <div className={styles.header}>
            <span className={styles.title}>
              {contentProps.programType}
              {' — '}
              <Translation span text={KEYS.title} />
            </span>
          </div>
          <div className={styles.headerStatus}>
            <PlanningSmbStatus
              summary={currentOfferingSummary}
              baselineSummary={offeringBaseline}
            />
          </div>
        </div>
      )}
      content={PlanningSmbContent}
      contentProps={{
        ...contentProps,
        onSavingChange: handleSavingChange,
        onSummaryChange: setCurrentOfferingSummary,
      }}
      onClose={handleClose}
    />
  )
}
